import { useState } from "react";

import { ColocaAccordion, ColocaAccordionItem } from "@/shared/components/layout/ColocaAccordion";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { ACCESS_AREAS, areaModules } from "../../constants/accessAreas.constants";
import { cellKey, type PermissionCell } from "../../utils/permissionCells";

export interface AccessAreaCatalogProps<T> {
  /** Items that each stand for exactly one cell — the base roles or base policies. */
  items: readonly T[];
  cellOf: (item: T) => PermissionCell | null;
  getId: (item: T) => string;
  /** Tooltip for an item's chip: its real name, and whatever else the reader needs before opening it. */
  describe: (item: T) => string;
  onOpen: (item: T) => void;
  /** Filters modules by label or id; matching areas are forced open. */
  query?: string;
}

/**
 * The one-permission roles or policies of the catalog, read as the 8 business areas: one row per module and a
 * chip per action (Ver / Editar / Eliminar) that opens the item. It replaces a wall of ~86 near-identical
 * cards ("Cards Read", "Cards Write"…) with eight collapsed lines, and the reader still reaches every item.
 *
 * Read-only browser: it opens items, it never assigns or edits anything itself.
 */
export function AccessAreaCatalog<T>({
  items,
  cellOf,
  getId,
  describe,
  onOpen,
  query = "",
}: AccessAreaCatalogProps<T>) {
  const { t } = useTranslations("settings");
  const [openAreas, setOpenAreas] = useState<Record<string, boolean>>({});
  const term = query.trim().toLowerCase();

  const byCell = new Map<string, T[]>();
  for (const item of items) {
    const cell = cellOf(item);
    if (!cell) continue;
    const key = cellKey(cell.moduleId, cell.atom);
    byCell.set(key, [...(byCell.get(key) ?? []), item]);
  }

  const areas = ACCESS_AREAS.map((area) => {
    const areaMatches = !term || t(area.labelKey).toLowerCase().includes(term);
    const modules = areaModules(area).filter(
      (mod) =>
        mod.actions.some((atom) => byCell.has(cellKey(mod.id, atom))) &&
        (areaMatches || t(mod.labelKey).toLowerCase().includes(term) || mod.id.includes(term)),
    );
    const count = modules.reduce(
      (n, mod) =>
        n + mod.actions.reduce((m, atom) => m + (byCell.get(cellKey(mod.id, atom))?.length ?? 0), 0),
      0,
    );
    return { area, modules, count };
  }).filter(({ modules }) => modules.length > 0);

  if (areas.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border bg-muted/30 px-4 py-5 text-center text-[12.5px] text-muted-foreground">
        {t("access_catalog_no_matches")}
      </div>
    );
  }

  return (
    <ColocaAccordion>
      {areas.map(({ area, modules, count }) => (
        <ColocaAccordionItem
          key={area.id}
          title={t(area.labelKey)}
          icon={area.icon}
          open={term ? true : (openAreas[area.id] ?? false)}
          onOpenChange={(next) => {
            if (!term) setOpenAreas((prev) => ({ ...prev, [area.id]: next }));
          }}
          meta={
            <span className="font-mono text-[10.5px] tabular-nums text-muted-foreground/70">{count}</span>
          }
          contentClassName="p-0"
        >
          <p className="border-b border-border/60 px-3 py-2.5 text-[11.5px] leading-snug text-muted-foreground">
            {t(area.hintKey)}
          </p>
          <ul className="m-0 list-none divide-y divide-border/50 p-0">
            {modules.map((mod) => {
              const ModIcon = mod.icon;
              const label = t(mod.labelKey);
              return (
                <li key={mod.id} className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 px-3 py-2">
                  <span className="flex min-w-0 flex-1 basis-40 items-center gap-2">
                    <ModIcon size={13} className="shrink-0 text-muted-foreground" aria-hidden />
                    <span className="truncate text-[12.5px] font-medium text-foreground" title={mod.id}>
                      {label}
                    </span>
                  </span>
                  <span className="ml-auto flex shrink-0 flex-wrap justify-end gap-1">
                    {mod.actions.map((atom) => {
                      const cellItems = byCell.get(cellKey(mod.id, atom)) ?? [];
                      if (cellItems.length === 0) {
                        return (
                          <span
                            key={atom}
                            aria-hidden
                            className="inline-flex min-h-7 min-w-[4.25rem] items-center justify-center rounded-md border border-dashed border-border px-2 text-[11.5px] text-muted-foreground/40"
                          >
                            {t(`access_level_${atom}`)}
                          </span>
                        );
                      }
                      return cellItems.map((item) => (
                        <button
                          key={getId(item)}
                          type="button"
                          title={describe(item)}
                          aria-label={`${label}: ${t(`access_level_${atom}`)} — ${describe(item)}`}
                          onClick={() => onOpen(item)}
                          className={cn(
                            "inline-flex min-h-7 min-w-[4.25rem] items-center justify-center rounded-md border border-border bg-card px-2 text-[11.5px] text-foreground transition-colors",
                            "hover:border-primary/50 hover:bg-primary/5 hover:text-primary",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                          )}
                        >
                          {t(`access_level_${atom}`)}
                        </button>
                      ));
                    })}
                  </span>
                </li>
              );
            })}
          </ul>
        </ColocaAccordionItem>
      ))}
    </ColocaAccordion>
  );
}
